import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { ScrollView, View } from 'react-native';

import constants from '../../Config/constants';
import { isScrollEndReached } from '../../Helper/Routines';
import ThemeStyle, { globalSpacing } from '../../Theme/styles';

import ArrowControl from '../ArrowControl';
import List from '../List';

// this component can be use on 'any' listing as long as it's horizontal
// this component also include ArrowControl for web
// getMarginStyle is commonly used margin for horizontal listing both mobile and web
const getMarginStyle = (
  isFirst,
  isLast,
  noMargin = constants.isWeb,
  spacing,
) => {
  const firstAndLastMargin = noMargin ? 0 : globalSpacing;
  return {
    marginLeft: isFirst ? firstAndLastMargin : spacing || globalSpacing / 4,
    marginRight: isLast ? firstAndLastMargin : spacing || globalSpacing / 4,
  };
};
const HorizontalListing = forwardRef(
  ({ leftContent, rightContent, ...props }, ref) => {
    const list = useRef();
    const scrollViewWidth = useRef(0);
    const [isEndReached, setEndReached] = useState(false);
    const [scrollOffset, setScrollOffset] = useState(0);
    const hasRightContent = !!rightContent;

    useImperativeHandle(ref, () => ({ listRef: list.current }));

    // effect for when the list data size changed, reset the isEndReached state to initial value
    useEffect(() => {
      setEndReached(false);
    }, [props?.data?.length]);

    // use on scrollview for onload if the horizonta list is not scrollable
    const _onContentSizeChange = (width) => {
      if (width <= scrollViewWidth.current) {
        setEndReached(true);
      }
    };
    // use on scrollview for onload if the horizonta list is not scrollable
    const _onLayoutOfScrollView = (event) => {
      scrollViewWidth.current = event.nativeEvent.layout.width;
    };
    // use on scrollview
    const _onScroll = useCallback(
      ({ nativeEvent }) => {
        // gets called every scroll
        const isEnd = isScrollEndReached(nativeEvent);
        if (isEnd && !isEndReached) {
          // so we add flag that it should set the isEndReached to true if the list is at the end and isReachedEnd is stil false
          setEndReached(true);
        }
        setScrollOffset(nativeEvent?.contentOffset?.x);
      },
      [isEndReached],
    );

    const _onEndReached = useCallback(() => {
      // always get called when end reached (e.g on end then scroll back, then scroll to end again)
      if (!isEndReached) {
        // so we add flag that it should set the isEndReached to true if isReachedEnd is stil false
        setEndReached(true);
      }
    }, [isEndReached]);

    const _renderList = useCallback(() => {
      const conditionalProps = constants.isWeb
        ? {
            scrollEventThrottle: constants.scrollThrottle,
            onEndReached: _onEndReached,
            onScroll: _onScroll,
          }
        : {};
      if (props.useScrollView) {
        const { data, renderItem, ...rest } = props;
        return (
          <ScrollView
            {...rest}
            {...conditionalProps}
            ref={list}
            onContentSizeChange={_onContentSizeChange}
            onLayout={_onLayoutOfScrollView}
            showsHorizontalScrollIndicator={false}
            horizontal
          >
            {data.map((item, index) =>
              renderItem({
                index,
                isFirstOfList: index === 0,
                isLastOfList: index !== data.length - 1,
                item,
              }),
            )}
          </ScrollView>
        );
      } else {
        return <List {...props} {...conditionalProps} ref={list} horizontal />;
      }
    }, [props, _onEndReached, _onScroll]);

    return (
      <Fragment>
        <View
          style={[
            ThemeStyle.flexDirectionColEnd,
            ThemeStyle.flexDirectionRowSpaceBetween,
            ThemeStyle.spacingBottomSmall,
          ]}
        >
          {/* Left Content */}
          <View
            style={[
              ThemeStyle.flex1,
              hasRightContent && ThemeStyle.spacingRight,
            ]}
          >
            {leftContent}
          </View>

          {/* Arrow Control & Right Content */}
          <View
            style={[
              ThemeStyle.flexDirectionRowCenter,
              constants.isWeb && ThemeStyle.spacingBottomSmall,
            ]}
          >
            {!!hasRightContent && (
              <View style={ThemeStyle.spacingRight}>{rightContent}</View>
            )}
          </View>
        </View>
        {constants.isWeb ? (
          <ArrowControl
            listRef={list}
            scrollOffset={scrollOffset}
            isEndReached={isEndReached}
            position="middle"
            withBackground
            {...props}
          >
            {_renderList()}
          </ArrowControl>
        ) : (
          _renderList()
        )}
      </Fragment>
    );
  },
);

export { getMarginStyle };
export default HorizontalListing;
